import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faCheckCircle, faExclamationTriangle, faClock, faHourglassHalf } from '@fortawesome/free-solid-svg-icons';
import { formatProcessingState, getProcessingStateColor } from '@/utils/videoProcessing';
import type { Video, VideoProcessingState } from '@/models';

interface VideoProcessingStatusProps {
  video: Video;
  compact?: boolean;
}

const VideoProcessingStatus: React.FC<VideoProcessingStatusProps> = ({ video, compact = false }) => {
  const getStateIcon = (state: VideoProcessingState) => {
    switch (state) {
      case 'WAITING':
        return faClock;
      case 'QUEUED':
        return faHourglassHalf;
      case 'ACTIVE':
        return faSpinner;
      case 'DONE':
        return faCheckCircle;
      case 'ERROR':
        return faExclamationTriangle;
      default:
        return faClock;
    }
  };

  const getProcessingStepDescription = (step: string) => {
    switch (step) {
      case 'Lokale Verarbeitung':
        return 'Lokale Verarbeitung und extraktion des LLM Knowledge';
      case 'CDN Upload':
        return 'Upload zu CDN';
      case 'CDN Verarbeitung':
        return 'Video-Verarbeitung auf CDN Seite';
      default:
        return '';
    }
  };

  const getStateDescription = (state: VideoProcessingState, step: string, processingMessage?: { timestamp: string; message: string } | null) => {
    const stepDesc = getProcessingStepDescription(step);
    let description = '';
    
    switch (state) {
      case 'WAITING':
        description = `${stepDesc} - Wartet auf Start`;
        break;
      case 'QUEUED':
        description = `${stepDesc} - In Warteschlange`;
        break;
      case 'ACTIVE':
        description = `${stepDesc} - Läuft gerade`;
        // Add processing message if available and this is local processing
        if (step === 'Lokale Verarbeitung' && processingMessage) {
          const timestamp = new Date(processingMessage.timestamp).toLocaleString('de-DE');
          description += `\n\n${processingMessage.message}\n(${timestamp})`;
        }
        break;
      case 'DONE':
        description = `${stepDesc} - Erfolgreich abgeschlossen`;
        break;
      case 'ERROR':
        description = `${stepDesc} - Fehler aufgetreten`;
        break;
      default:
        description = stepDesc;
    }
    
    return description;
  };

  const states = [
    { label: 'CDN Upload',          state: video.cdnUploadState },
    { label: 'CDN Verarbeitung',    state: video.cdnProcessingState },
    { label: 'Lokale Verarbeitung', state: video.localProcessingState, processingMessage: video.localProcessingMessage },
  ];

  if (compact) {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        {states.map(({ label, state, processingMessage }) => (
          <div 
            key={label} 
            className={`px-2 py-1 rounded-full text-xs font-medium cursor-help ${getProcessingStateColor(state)}`}
            title={getStateDescription(state, label, processingMessage)}
          >
            <FontAwesomeIcon
              icon={getStateIcon(state)}
              className={`w-3 h-3 mr-1 ${state === 'ACTIVE' ? 'animate-spin' : ''}`}
            />
            {formatProcessingState(state)}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        Verarbeitungsstatus
      </h4>
      {states.map(({ label, state, processingMessage }) => (
        <div key={label} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <span 
            className="text-sm text-gray-600 dark:text-gray-400 cursor-help" 
            title={getProcessingStepDescription(label)}
          >
            {label}
          </span>
          <div 
            className={`px-2 py-1 rounded-full text-xs font-medium cursor-help ${getProcessingStateColor(state)}`}
            title={getStateDescription(state, label, processingMessage)}
          >
            <FontAwesomeIcon
              icon={getStateIcon(state)}
              className={`w-3 h-3 mr-1 ${state === 'ACTIVE' ? 'animate-spin' : ''}`}
            />
            {formatProcessingState(state)}
          </div>
        </div>
      ))}
    </div>
  );
};

export default VideoProcessingStatus;